import {
  type AllDualVariantIconKeys,
  Body,
  Box,
  Button,
  Caption,
  Icon,
  ShimmerBox,
  Stack,
} from "@biom3/react";
import Link from "next/link";
import type { ReactNode } from "react";
import type {
  GemRewardPrize,
  RewardEarningPrize,
  TokenRewardPrize,
} from "../../domain/gems-game/domain/lib/Earning";

type RedemptionRewardsProps = {
  claimedRewards?: RewardEarningPrize[];
  showHistory?: boolean;
};

type Reward = {
  id: string;
  title: string;
  amount: string | ReactNode;
  icon: AllDualVariantIconKeys;
  isJackpot?: boolean;
};

export const RedemptionRewards = ({
  claimedRewards,
  showHistory,
}: RedemptionRewardsProps) => {
  const mappedRewards = claimedRewards?.reduce<Reward[]>((acc, reward) => {
    switch (reward.type) {
      case "token": {
        const tokenReward = reward as TokenRewardPrize;

        if (
          tokenReward.metadata.amount === "0" ||
          !tokenReward.metadata.amount
        ) {
          break;
        }

        const isJackpot =
          tokenReward.metadata.reward_source === "jackpot" ||
          tokenReward.metadata.is_jackpot;
        const isReferral = tokenReward.metadata.reward_source === "referral";

        const getRewardTitle = () => {
          if (isJackpot) return "Mythic Reward";
          if (isReferral) return "Referral Reward";
          return "IMX Reward";
        };

        const formatter = new Intl.NumberFormat(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 3,
        });
        const formattedAmount = formatter.format(
          Number(tokenReward.metadata.amount),
        );

        const rewardItem: Reward = {
          icon: "ImxToken" as AllDualVariantIconKeys,
          title: getRewardTitle(),
          amount: `${formattedAmount} IMX`,
          id: isJackpot
            ? "mythic-reward"
            : isReferral
              ? "referral-reward"
              : "imx",
          isJackpot,
        };

        // Ensure jackpot rewards at the beginning
        if (isJackpot) {
          acc.unshift(rewardItem);
        } else {
          acc.push(rewardItem);
        }
        break;
      }
      case "point": {
        const gemReward = reward as GemRewardPrize;

        if (gemReward.metadata?.reward_source === "referral") {
          break;
        }

        acc.push({
          icon: "ImxRewards",
          title: "Gems",
          amount: gemReward.metadata.amount.toString(),
          id: "gems",
        });
        break;
      }
      case "sweepstake_free_entry": {
        acc.push({
          // @ts-expect-error doesn't have dual variant
          icon: "Key",
          title: "Free Entry",
          amount: "1",
          id: "free-key",
        });
        break;
      }
      case "sweepstake_ticket_upgrade": {
        acc.push({
          // @ts-expect-error doesn't have dual variant
          icon: "Key",
          title: "Key Tier-Up",
          amount: "x1",
          id: "upgrade-key",
        });
        break;
      }
    }
    return acc;
  }, []);

  const referralPointRewards = claimedRewards?.filter((reward) => {
    return (
      reward.type === "point" &&
      (reward as GemRewardPrize).metadata?.reward_source === "referral"
    );
  }) as GemRewardPrize[] | undefined;

  if (referralPointRewards && referralPointRewards.length > 0) {
    const referralAmount = referralPointRewards[0].metadata.amount;
    const referralCount = referralPointRewards.length;

    const referralReward: Reward = {
      icon: "People" as AllDualVariantIconKeys,
      title: "Referral Rewards",
      amount: (
        <>
          {referralAmount}
          <Icon
            variant="bold"
            icon="ImxRewards"
            sx={{
              fill: "white",
              width: "16px",
              height: "16px",
            }}
          />
          x {referralCount}
        </>
      ),
      id: "referral-bonus",
    };

    mappedRewards?.push(referralReward);
  }

  return (
    <Stack
      direction="column"
      gap="base.spacing.x2"
      sx={{
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(5rem, 1fr))",
          gap: "base.spacing.x2",
          pt: "base.spacing.x4",
        }}
      >
        <Stack
          direction={{ default: "column", large: "row" }}
          alignItems="center"
        >
          {mappedRewards?.length && mappedRewards.length > 0 ? (
            <Stack
              direction="row"
              sx={{ flex: mappedRewards.length === 1 ? "none" : 1 }}
            >
              {mappedRewards.map((reward, index) => (
                <Stack
                  sx={{
                    padding: "base.spacing.x4",
                    backgroundColor: reward.isJackpot
                      ? "white"
                      : "base.color.translucent.emphasis.100",
                    borderRadius: "base.borderRadius.x7",
                    flex: mappedRewards.length === 1 ? "none" : 1,
                    minWidth: mappedRewards.length === 1 ? "150px" : "none",
                    boxShadow: reward.isJackpot ? "0 0 1rem white" : "none",
                  }}
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  gap="base.spacing.x1"
                  key={`${reward.id}-${index}}` || `${reward.icon}-${index}`}
                >
                  <Icon
                    variant="bold"
                    sx={{
                      fill: reward.isJackpot ? "base.gradient.1" : "white",
                      width: "base.icon.size.400",
                      height: "base.icon.size.400",
                    }}
                    icon={reward.icon}
                  />
                  <Caption
                    size={"small"}
                    weight={"bold"}
                    sx={{
                      textAlign: "center",
                      color: "base.color.text.body.secondary",
                    }}
                  >
                    {reward.title.toUpperCase()}
                  </Caption>
                  <Body
                    size="small"
                    weight="bold"
                    sx={{
                      textAlign: "center",
                      color: reward.isJackpot
                        ? "base.color.text.heading.inverse.secondary"
                        : "base.color.text.body.primary",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "4px",
                    }}
                  >
                    {reward.amount}
                  </Body>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Body size="large" weight="bold">
              <ShimmerBox
                sx={{
                  height: "64px",
                }}
              />
            </Body>
          )}
          {showHistory && (
            <Button
              size={["small", "small", "large"]}
              variant="secondary"
              sx={{
                width: {
                  default: "100%",
                  medium: "inherit",
                },
              }}
              rc={<Link href="/rewards/history" />}
            >
              Show rewards history
            </Button>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};
